/**
 * Live Google Places photos for a Place ID (browser Maps JS API only).
 * Uses the same referrer-restricted client key as googleMapsLoader — never a server key.
 */

import { googleMapsLoader } from '@/utils/googleMapsLoader';
import type { LivePlacePhoto } from '@/utils/merchantStorefrontGallery';
import { extractPhotoReferenceFromUrl } from '@/utils/merchantStorefrontGallery';

const CACHE_PREFIX = 'vaiu:placePhotos:v1:';
/** Short TTL to avoid hammering Google on navigation */
const TTL_MS = 15 * 60 * 1000;

type CachePayload = { savedAt: number; items: LivePlacePhoto[] };

function readCache(placeId: string): LivePlacePhoto[] | null {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + placeId);
    if (!raw) return null;
    const p = JSON.parse(raw) as CachePayload;
    if (Date.now() - p.savedAt > TTL_MS) {
      sessionStorage.removeItem(CACHE_PREFIX + placeId);
      return null;
    }
    return Array.isArray(p.items) ? p.items : null;
  } catch {
    return null;
  }
}

function writeCache(placeId: string, items: LivePlacePhoto[]) {
  try {
    sessionStorage.setItem(
      CACHE_PREFIX + placeId,
      JSON.stringify({ savedAt: Date.now(), items } satisfies CachePayload)
    );
  } catch {
    /* quota / private mode */
  }
}

async function fetchLivePhotosUncached(placeId: string): Promise<LivePlacePhoto[]> {
  await googleMapsLoader.load({ libraries: ['places'] });
  const { Place } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
  const place = new Place({ id: placeId });
  await place.fetchFields({ fields: ['photos'] });
  const photos = place.photos || [];
  const items: LivePlacePhoto[] = [];

  photos.forEach((photo, idx) => {
    try {
      const url = photo.getURI({ maxHeight: 960, maxWidth: 1280 });
      if (!url?.trim()) return;
      const named = photo as { name?: string };
      const photoRef =
        (typeof named.name === 'string' && named.name.trim()) ||
        extractPhotoReferenceFromUrl(url) ||
        `${placeId}:photo:${idx}`;
      items.push({ url, photoRef });
    } catch {
      /* skip broken photo */
    }
  });

  return items;
}

/**
 * Cached live Place photos. On any failure returns [] (caller should fall back to API-only gallery).
 */
export async function fetchLiveGooglePlacePhotosWithCache(placeId: string): Promise<LivePlacePhoto[]> {
  const trimmed = placeId?.trim();
  if (!trimmed) return [];

  const hit = readCache(trimmed);
  if (hit) return hit;

  try {
    const items = await fetchLivePhotosUncached(trimmed);
    writeCache(trimmed, items);
    return items;
  } catch {
    return [];
  }
}
